/**
 * API service for marketing demo submissions
 */

import { BASE_SERVER_URL } from '../utils/constants';
import type { ModuleType, DemoResponse } from '../types/demo';
import logger from '../utils/logger';

const API_BASE = `${BASE_SERVER_URL}/api/v1/demo`;

export interface SubmitDemoParams {
  moduleType: ModuleType;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  file?: File;
  files?: File[]; // For modules that support multiple files (e.g., lease-abstract)
}

/**
 * Submit a document for demo analysis
 * @returns Promise resolving to the API response
 */
export async function submitDemo({
  moduleType,
  firstName,
  lastName,
  email,
  company,
  file,
  files
}: SubmitDemoParams): Promise<DemoResponse> {
  const formData = new FormData();
  formData.append('firstName', firstName);
  formData.append('lastName', lastName);
  formData.append('email', email);
  formData.append('company', company);

  // Handle multiple files (e.g., lease-abstract) or single file
  if (files && files.length > 0) {
    files.forEach(f => formData.append('files', f));
  } else if (file) {
    formData.append('file', file);
  }

  try {
    const response = await fetch(`${API_BASE}/submit/${moduleType}`, {
      method: 'POST',
      body: formData
    });

    const data = await response.json();

    // Handle different response types
    if (response.status === 429) {
      // Rate limited
      return {
        success: false,
        error: 'rate_limited',
        message: data.message || 'Rate limit exceeded',
        retryAfter: data.retryAfter || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      };
    }

    if (response.status === 400) {
      // Validation error
      return {
        success: false,
        error: data.error || 'validation_error',
        message: data.message || 'Invalid request'
      };
    }

    if (!response.ok) {
      // Server error
      return {
        success: false,
        error: data.error || 'server_error',
        message: data.message || 'An unexpected error occurred'
      };
    }

    // Success
    return {
      success: true,
      message: data.message || 'Your document is being processed',
      submissionId: data.submissionId
    };
  } catch (error) {
    logger.error('Demo submission error:', error);

    // Network error
    return {
      success: false,
      error: 'network_error',
      message: 'Unable to connect. Please check your internet connection and try again.'
    };
  }
}

/**
 * Get accepted file types as a comma-separated string for input accept attribute
 */
export function getAcceptedFileTypes(): string {
  return '.pdf,.xlsx,.xls';
}

/**
 * Validate file before submission
 * @param file - The file to validate
 * @param acceptedExtensions - Optional list of accepted extensions (e.g., ['.pdf', '.xlsx']). If not provided, defaults to all supported types.
 */
export function validateFile(file: File, acceptedExtensions?: string[]): { valid: boolean; error?: string } {
  const maxSizeMB = 50;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds ${maxSizeMB}MB limit`
    };
  }

  // Default to all supported types if not specified
  const extensions = acceptedExtensions || ['.pdf', '.xlsx', '.xls'];

  // Map extensions to MIME types
  const extensionToMimeType: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.xls': 'application/vnd.ms-excel'
  };

  const allowedMimeTypes = extensions
    .map(ext => extensionToMimeType[ext])
    .filter(Boolean);

  // Check by extension as a fallback
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

  if (!allowedMimeTypes.includes(file.type) && !extensions.includes(fileExtension)) {
    // Generate user-friendly error message based on accepted types
    const typeNames = extensions.map(ext => ext.replace('.', '').toUpperCase()).join(' or ');
    return {
      valid: false,
      error: `Please upload a ${typeNames} file`
    };
  }

  return { valid: true };
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return bytes + ' B';
  } else if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(1) + ' KB';
  } else {
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
}
